import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardBody, Col, Container, Label, Row } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Flatpickr from "react-flatpickr";
import { useAppDispatch } from "Components/Hooks/hooks";
import { useAppSelector } from "Components/Hooks/hooks";
import { useDebounce } from "Components/Hooks/useDebounce";
import CustomInput from "Components/Custom/CustomInput";
import CustomButton from "Components/Custom/CustomButton";
import Icon from "Components/Icons/Icon";
import {
  getClients,
  getInvoices,
  getPaymentDepositQuotes,
  createPayment,
  sharePayment,
} from "slices/thunks";
import CustomShareModal from "Components/Modals/CustomShareModal";
import { LinkPayload } from "types/payment.api";
import { VALIDATION_MESSAGES } from "Components/constants/validation";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import { formatDateToYMD } from "utils/formatDates";
import { ErrorHandler } from "utils/toast";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import AlertModal from "Components/Modals/AlertModal";

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  label: string;
  value: string;
}

interface RecordPaymentFormValues {
  client: string;
  type: string;
  quote_id: string;
  invoice_id: string;
  amount: string;
  date: string;
  method: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAYMENT_METHODS: Array<{ label: string; value: string | number }> = [
  { label: "Cash", value: "cash" },
  { label: "Stripe", value: "stripe" },
];

const PAYMENT_TYPES = ["Deposit", "Invoice"] as const;

// ─── Validation ───────────────────────────────────────────────────────────────

const validationSchema = Yup.object<RecordPaymentFormValues>({
  client: Yup.string().when("type", {
    is: "Deposit",
    then: (schema) =>
      schema.required(VALIDATION_MESSAGES.SELECT_REQUIRED("client")),
    otherwise: (schema) => schema.optional(),
  }),
  type: Yup.string().required(
    VALIDATION_MESSAGES.SELECT_REQUIRED("payment type"),
  ),
  quote_id: Yup.string().optional(),
  amount: Yup.number().when("type", {
    is: "Deposit",
    then: (schema) =>
      schema
        .required(VALIDATION_MESSAGES.REQUIRED("amount"))
        .positive("Amount must be greater than 0"),
    otherwise: (schema) => schema.optional(),
  }),
  invoice_id: Yup.string().when("type", {
    is: "Invoice",
    then: (schema) =>
      schema.required(VALIDATION_MESSAGES.SELECT_REQUIRED("invoice")),
    otherwise: (schema) => schema.optional(),
  }),
  date: Yup.string().required(VALIDATION_MESSAGES.REQUIRED("date")),
  method: Yup.string().required(
    VALIDATION_MESSAGES.SELECT_REQUIRED("payment method"),
  ),
});

// ─── Component ────────────────────────────────────────────────────────────────

const RecordPayment = () => {
  document.title = "Record Payment | EaziQuote";

  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const dispatch = useAppDispatch();

  const { clients = [], isLoading: clientsLoading } = useAppSelector(
    (state) => state.client,
  );
  const { invoices = [], status: invoiceStatus } = useAppSelector(
    (state) => state.invoice,
  );
  const user = useAppSelector((state) => state.auth.user);

  const { status, depositQuotes = [] } = useAppSelector(
    (state) => state.payment,
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [invoiceSearch, setInvoiceSearch] = useState<string>("");
  const debouncedInvoiceSearch = useDebounce(invoiceSearch, 500);
  const [quoteSearch, setQuoteSearch] = useState<string>("");
  const debouncedQuoteSearch = useDebounce(quoteSearch, 500);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [createdPaymentData, setCreatedPaymentData] =
    useState<LinkPayload | null>(null);
  const [cashConfirmOpen, setCashConfirmOpen] = useState(false);
  const [pendingCashPayment, setPendingCashPayment] =
    useState<RecordPaymentFormValues | null>(null);

  useEffect(() => {
    dispatch(getClients({ search: debouncedSearch }));
  }, [debouncedSearch, dispatch]);

  // Auto-select client passed from navigation state once clients are loaded
  useEffect(() => {
    if (
      locationState?.client_id &&
      clients.length > 0 &&
      !formik.values.client
    ) {
      const match = clients.find((c) => c.id === locationState.client_id);
      if (match) {
        formik.setFieldValue("client", String(match.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, locationState?.client_id]);

  const [isShowStripeConnectModal, setIsShowStripeConnectModal] =
    useState(false);

  // Auto-select invoice passed from navigation state once invoices are loaded
  useEffect(() => {
    if (
      locationState?.invoice_id &&
      invoices.length > 0 &&
      !formik.values.invoice_id
    ) {
      const match = invoices.find((inv) => inv.id === locationState.invoice_id);
      if (match) {
        formik.setFieldValue("invoice_id", String(match.id));
        formik.setFieldValue("method", "cash", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, locationState?.invoice_id]);

  const clientOptions: SelectOption[] = clients.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  const invoiceOptions: SelectOption[] = invoices.map((inv) => ({
    label: `${inv.reference_number} — ${inv.title}`,
    value: String(inv.id),
  }));

  const quoteOptions: SelectOption[] = [
    ...depositQuotes.map((quote) => ({
      label: `${quote.reference_number} — ${quote.title}`,
      value: String(quote.id),
    })),
  ];

  const getSelectedInvoice = (invoiceId: string) =>
    invoices.find((inv) => inv.id.toString() === invoiceId);

  const getSelectedQuote = (quoteId: string) =>
    depositQuotes.find((quote) => quote.id.toString() === quoteId);

  const getQuoteDueAmount = (quoteId: string) => {
    const selectedQuote = getSelectedQuote(quoteId);
    if (!selectedQuote) return 0;

    return Number(selectedQuote.deposit_amount ?? 0);
  };

  const submitPayment = async (values: RecordPaymentFormValues) => {
    if (
      values.method === "stripe" &&
      user?.stripe_account_status !== "active"
    ) {
      setIsShowStripeConnectModal(true);
      return;
    }

    const paymentAmount =
      values.type === "Invoice"
        ? Number(getSelectedInvoice(values.invoice_id)?.total_due ?? 0)
        : values.quote_id
        ? getQuoteDueAmount(values.quote_id)
        : Number(values.amount);

    const payload = {
      ...(values.client && { client_id: Number(values.client) }),
      amount: paymentAmount,
      amount_type: values?.type?.toLowerCase(),
      payment_method: values.method,
      currency: "GBP",
      payment_date: values.date,
      order_id: values.type === "Deposit" ? values.quote_id || "" : "",
      ...(values.type === "Invoice" && {
        invoice_id: Number(values.invoice_id),
      }),
    };

    try {
      const result = await dispatch(createPayment(payload)).unwrap();
      if (
        values.method === "stripe" &&
        result?.payload?.payment?.stripe_payment_link_url !== null
      ) {
        setCreatedPaymentData(result.payload?.payment);
        console.log("created payment", result.payload);

        setShareModalOpen(true);
      } else {
        navigate(-1);
      }
    } catch (error) {
      ErrorHandler(error);
    }
  };

  const formik = useFormik<RecordPaymentFormValues>({
    initialValues: {
      client: "",
      type: locationState?.invoice_id ? "Invoice" : "Deposit",
      quote_id: "",
      invoice_id: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      method: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (values.method === "cash") {
        setPendingCashPayment(values);
        setCashConfirmOpen(true);
        return;
      }
      await submitPayment(values);
    },
  });

  useEffect(() => {
    if (formik.values.type !== "Deposit" || !formik.values.client) return;

    dispatch(
      getPaymentDepositQuotes({
        client_id: Number(formik.values.client),
        search: debouncedQuoteSearch,
      }),
    );
  }, [
    debouncedQuoteSearch,
    dispatch,
    formik.values.client,
    formik.values.type,
  ]);

  useEffect(() => {
    if (formik.values.type === "Invoice") {
      dispatch(getInvoices({ search: debouncedInvoiceSearch, is_not_paid: 1 }));
      return;
    }
  }, [debouncedInvoiceSearch, dispatch, formik.values.type]);

  const handleConfirmCashPayment = async () => {
    if (!pendingCashPayment) {
      setCashConfirmOpen(false);
      return;
    }

    const valuesToSubmit = pendingCashPayment;
    setCashConfirmOpen(false);
    setPendingCashPayment(null);
    await submitPayment(valuesToSubmit);
  };

  const handleCancelCashPayment = () => {
    setCashConfirmOpen(false);
    setPendingCashPayment(null);
  };

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className="gap-3 d-flex flex-column">
            <PageHeaderActions
              title="Record Payment"
              subtitle=""
              actions={[]}
            />

            <Card className="br-10 bg-white">
              <CardBody className="p-4">
                <form onSubmit={formik.handleSubmit}>
                  {/* AMOUNT + TYPE */}
                  <div className="mb-3">
                    <Label className="font-primary fw-normal fs-14 text-black2D">
                      Amount
                    </Label>
                    {/* Error is OUTSIDE this flex row — so it can never affect toggle position */}
                    <div className="d-flex gap-3 align-items-center">
                      {/* Toggle — fixed size, completely isolated */}
                      <div
                        className="d-flex align-items-center flex-shrink-0"
                        style={{
                          border: "2px solid #D1D5DB",
                          borderRadius: "7px",
                          padding: "3px",
                          background: "#fff",
                          height: "44px",
                          width: "210px",
                        }}
                      >
                        {PAYMENT_TYPES.map((t) => {
                          const isActive = formik.values.type === t;
                          return (
                            <button
                              key={t}
                              type="button"
                              onClick={() => {
                                formik.resetForm({
                                  values: {
                                    client: "",
                                    type: t,
                                    quote_id: "",
                                    invoice_id: "",
                                    amount: "",
                                    date: new Date()
                                      .toISOString()
                                      .split("T")[0],
                                    method: "",
                                  },
                                });
                                setSearchTerm("");
                                setInvoiceSearch("");
                                setQuoteSearch("");
                              }}
                              disabled={status.add === "loading"}
                              className="border-0 d-flex align-items-center justify-content-center"
                              style={{
                                flex: 1,
                                height: "100%",
                                borderRadius: "5px",
                                background: isActive
                                  ? "#082B60"
                                  : "transparent",
                                color: isActive ? "#fff" : "#1F2937",
                                fontSize: "14px",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                              }}
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* CLIENT */}
                  {formik.values.type === "Deposit" && (
                    <>
                      <CustomInput
                        name="client"
                        label="Client"
                        type="select"
                        placeholder="Search or select a client"
                        value={formik.values.client}
                        onChange={(value) => {
                          formik.handleChange(value);
                          formik.setFieldValue("quote_id", "", false);
                          formik.setFieldTouched("quote_id", false, false);
                          formik.setFieldValue("invoice_id", "", false);
                          formik.setFieldValue("amount", "", false);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.errors.client}
                        touched={formik.touched.client}
                        dropdownOptions={clientsLoading ? [] : clientOptions}
                        onSearch={setSearchTerm}
                        isLoadingOptions={clientsLoading}
                      />

                      <CustomInput
                        name="quote_id"
                        label="Select Quote"
                        type="select"
                        placeholder={
                          formik.values.client
                            ? "Search or Select a quote"
                            : "Select client first"
                        }
                        value={formik.values.quote_id}
                        onChange={(value) => {
                          formik.handleChange(value);
                          formik.setFieldValue("invoice_id", "", false);
                          formik.setFieldValue("method", "cash", true);
                          const quoteId = value?.target?.value;
                          if (quoteId) {
                            const amt = getQuoteDueAmount(quoteId);
                            formik.setFieldValue("amount", String(amt), true);
                          } else {
                            formik.setFieldValue("amount", "", true);
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.errors.quote_id}
                        touched={formik.touched.quote_id}
                        dropdownOptions={
                          !formik.values.client ||
                          status.fetchDepositQuotes === "loading"
                            ? []
                            : quoteOptions
                        }
                        onSearch={setQuoteSearch}
                        isLoadingOptions={
                          !!formik.values.client &&
                          status.fetchDepositQuotes === "loading"
                        }
                      />

                      <CustomInput
                        name="amount"
                        label="Amount"
                        type="number"
                        placeholder="Enter amount"
                        value={formik.values.amount}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.amount}
                        touched={formik.touched.amount}
                        isDisabled={!!formik.values.quote_id}
                        isPound={true}
                      />

                      {formik.values.quote_id && (
                        <p className="fw-16 text-danger fw-normal mt-n2 font-primary">
                          Amount Due: £
                          {getQuoteDueAmount(formik.values.quote_id)}
                        </p>
                      )}
                    </>
                  )}

                  {/* SELECT INVOICE — only when type === "Invoice" */}
                  {formik.values.type === "Invoice" && (
                    <>
                      <CustomInput
                        name="invoice_id"
                        label="Select Invoice"
                        type="select"
                        placeholder="Select an invoice"
                        value={formik.values.invoice_id}
                        onChange={(value) => {
                          formik.handleChange(value);
                          formik.setFieldValue("method", "cash", true);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.errors.invoice_id}
                        touched={formik.touched.invoice_id}
                        dropdownOptions={
                          invoiceStatus.fetch === "loading"
                            ? []
                            : invoiceOptions
                        }
                        onSearch={setInvoiceSearch}
                        isLoadingOptions={invoiceStatus.fetch === "loading"}
                      />

                      {formik.values.invoice_id && (
                        <p className="fw-16 text-danger fw-normal mt-n2 font-primary">
                          Amount Due: £
                          {getSelectedInvoice(formik.values.invoice_id)
                            ?.total_due ?? 0}
                        </p>
                      )}
                    </>
                  )}

                  {/* DATE + METHOD */}
                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <Label className="font-primary fw-normal fs-14 text-black2D">
                          Date
                        </Label>
                        <div className="position-relative">
                          <Flatpickr
                            className="form-control auth-form-control"
                            placeholder="YYYY-MM-DD"
                            value={formik.values.date}
                            onChange={(date: any) => {
                              if (date[0]) {
                                formik.setFieldValue(
                                  "date",
                                  formatDateToYMD(date[0].toISOString()),
                                  true,
                                );
                                formik.setFieldTouched("date", true, false);
                              }
                            }}
                            options={{
                              dateFormat: "Y-m-d", // actual stored format
                              altInput: true,
                              altFormat: "d M Y", // display format -> 12 Apr 2026
                              disableMobile: true,
                              minDate: "today",
                            }}
                          />
                          <Icon
                            name="Calendar"
                            className="position-absolute"
                            style={{
                              right: 12,
                              top: "50%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          />
                        </div>
                        {formik.touched.date && formik.errors.date && (
                          <div className="text-danger fs-12 mt-1">
                            {formik.errors.date}
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <CustomInput
                        name="method"
                        label="Method"
                        type="select"
                        placeholder="Select method"
                        value={formik.values.method}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.method}
                        touched={formik.touched.method}
                        dropdownOptions={PAYMENT_METHODS}
                        isDisabled={!!formik.values.quote_id}
                      />
                      {formik.values.method === "cash" && (
                        <p className="fw-16 text-orangeFF fw-normal mt-n2 font-primary">
                          You are about to record a cash payment. Please ensure
                          the amount has been physically received before
                          proceeding.
                        </p>
                      )}
                    </Col>
                  </Row>

                  {formik.values.type === "Invoice" &&
                    formik.values.invoice_id &&
                    (() => {
                      const selectedInvoice = getSelectedInvoice(
                        formik.values.invoice_id,
                      );
                      const amountDue = selectedInvoice?.total_due ?? 0;
                      const depositAvailable =
                        selectedInvoice?.deposit_available ?? 0;
                      const fromThisPayment =
                        amountDue > depositAvailable
                          ? amountDue - depositAvailable
                          : 0;
                      const creditAfter = Math.max(
                        depositAvailable - amountDue,
                        0,
                      );
                      const fmt = (n: number) =>
                        `£${n.toLocaleString("en-GB", {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                        })}`;

                      return (
                        <>
                          <div className="divider mt-3 mb-3" />

                          <table className="table-borderless mb-4">
                            <tbody>
                              <tr className="d-flex justify-content-between gap-5 mt-2">
                                <td className="text-grey89 fw-normal fs-14 font-primary">
                                  Amount Due
                                </td>
                                <td className="text-end fw-normal fs-14 font-primary text-black2D">
                                  {fmt(amountDue)}
                                </td>
                              </tr>
                              <tr className="d-flex justify-content-between gap-5 mt-2">
                                <td className="text-grey89 fw-normal fs-14 font-primary">
                                  Deposit Available
                                </td>
                                <td className="text-end fw-normal fs-14 font-primary text-black2D">
                                  {fmt(depositAvailable)}
                                </td>
                              </tr>
                              <tr className="d-flex justify-content-between gap-5 mt-2">
                                <td className="text-grey89 fw-normal fs-14 font-primary">
                                  From This Payment
                                </td>
                                <td className="text-end fw-normal fs-14 font-primary text-black2D">
                                  {fmt(fromThisPayment)}
                                </td>
                              </tr>
                              <tr
                                className="d-flex justify-content-between gap-5 mt-2"
                                style={{ borderTop: "1px dashed #E5E7EB" }}
                              >
                                <td className="text-grey89 fw-normal fs-14 font-primary mt-2">
                                  Credit After Payment
                                </td>
                                <td className="text-end fw-normal fs-14 font-primary text-black2D mt-2">
                                  {fmt(creditAfter)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </>
                      );
                    })()}

                  {/* ACTIONS */}
                  <div className="d-flex gap-3 mt-2 ">
                    <CustomButton
                      text="Create Payment Record"
                      type="submit"
                      className="bg-primary px-4 py-2"
                      loading={status.add === "loading"}
                    />
                  </div>
                </form>
              </CardBody>
            </Card>
          </div>
        </Container>
      </div>

      <CustomShareModal
        isOpen={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          navigate(-1);
        }}
        title="Share Payment Link"
        shareUrl={createdPaymentData?.payment_link_url ?? ""}
        subject="Payment Link"
        recipientEmail={createdPaymentData?.client_email ?? ""}
        message={
          createdPaymentData?.client_name
            ? `Hi ${
                createdPaymentData.client_name
              }, here is your payment link:\n\n${
                createdPaymentData.payment_link_url ?? ""
              }\n\nThank you.`
            : `Here is your payment link:\n\n${
                createdPaymentData?.payment_link_url ?? ""
              }\n\nThank you.`
        }
        onSendEmail={() =>
          createdPaymentData?.id &&
          dispatch(sharePayment({ id: createdPaymentData.id }))
        }
        isSending={status.share === "loading"}
      />

      <RestrictedAccessModal
        isOpen={isShowStripeConnectModal}
        onCancel={() => setIsShowStripeConnectModal(false)}
        title="Payments & Processing Fees"
        description={`Online payments are securely processed through Stripe.

A 0.5% platform processing fee applies to all card and online payments processed through Stripe, including deposit payments. Standard Stripe transaction fees may also apply separately.

These fees are deducted internally from your payout, your customers will always pay the exact invoice or deposit amount shown to them.

Platform fees do not apply to cash payments, bank transfers, or offline payments.`}
        buttonText="Connect Stripe"
        onButtonClick={() => {
          setIsShowStripeConnectModal(false);
          navigate("/settings", { state: { focusStripe: true } });
        }}
        bodyClassName="text-start align-content-start"
        cancelText="Not Now"
      />

      <AlertModal
        isOpen={cashConfirmOpen}
        onCancel={handleCancelCashPayment}
        onConfirm={handleConfirmCashPayment}
        title="Confirm Cash Payment"
        description="By continuing, you confirm that the payment has already been received. This action will only record the payment in the system. Once recorded, it cannot be reversed or edited."
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={status.add === "loading"}
        icon={<Icon name="InfoPrimary" width={80} height={80} />}
      />
    </>
  );
};

export default RecordPayment;
